import { useCallback } from 'react';
import { Input } from 'src/common/components/input';
import { message } from 'src/common/components/message';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { HelperIconBox, helperLink } from 'src/components/helper-icon';
import { PRODUCT_NAME } from 'src/const/title';
import { useOpenDeleteSpace } from 'src/hooks/space';
import { getSpaceRolePermission } from 'src/hooks/space/use-get-space-role-permission';
import { getCurrentUser, useClearUser, useCurrentUser } from 'src/hooks/user';
import { $currentUserCache } from 'src/services/user/current-user';
import { ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { locationToUrl } from 'src/utils/location-utils';
import { CaptchaPanel } from './captcha-panel';
import { LogoffReason } from './logoff-reason';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

export interface LogoffReasonInfo {
  reasons: string[];
  otherReason?: string | undefined;
}

/** 注销流程，一个个对话框轮流弹 */
export const useOpenLogoffDialog = () => {
  const { t } = useAppTranslation();
  const openMustKnowDialog = useOpenMustKnowDialog();
  const openLogoutAccount = useOpenLogoutAccount();
  const openLogoffServiceDialog = useOpenLogoffServiceDialog();
  const openLogoffReasonDialog = useOpenLogoffReasonDialog();
  const openReceiveCaptchaDialog = useOpenReceiveCaptchaDialog();
  const openConfirmDialog = useOpenConfirmDialog();
  const clearUser = useClearUser();

  const open = async () => {
    try {
      const { nickname } = getCurrentUser();
      await openLogoutAccount();
      await openConfirmDialog(t('components.logoff.delete_account_and_data', { nickname }));
      if (!__BUILD_IN__) {
        await openMustKnowDialog();
        await openLogoffServiceDialog();
      }
      const reasonInfo = await openLogoffReasonDialog();
      await openReceiveCaptchaDialog(reasonInfo);
      const marks: string[] = [];
      reasonInfo.reasons.forEach((r, index) => {
        if (r) marks.push(index.toString());
      });
      reasonInfo.otherReason && marks.push(reasonInfo.otherReason);
      bizTracker.event('close_account', {
        is_success: true,
        close_reason: marks.join(';'),
      });
      message.success(t('components.logoff.logoff_success'));
      setTimeout(async () => {
        await clearUser();
        locationToUrl(ViewPath.login);
      }, 1000);
    } catch (error) {
      // do nothing
    }
  };
  return useCallback(open, [
    clearUser,
    openConfirmDialog,
    openLogoffReasonDialog,
    openLogoffServiceDialog,
    openLogoutAccount,
    openMustKnowDialog,
    openReceiveCaptchaDialog,
  ]);
};

/** 注销警告 */
const useOpenLogoutAccount = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  const openDeleteSpace = useOpenDeleteSpace();
  const currentUser = useCurrentUser();
  const { editor } = getSpaceRolePermission(currentUser.uuid);

  return useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      const { nickname } = getCurrentUser();
      openModal.warning({
        colorType: 'red',
        maxWidth: 400,
        title: t('components.logoff.about_to_logoff_account'),
        content: editor ? (
          <>
            <span>{t('components.logoff.will_destroy_data_click')}</span>
            <span
              className="text-red underline animate-click underline-offset-4 mx-1"
              onClick={openDeleteSpace}
            >
              {t('common.delete_space')}
            </span>
          </>
        ) : (
          t('components.logoff.will_destroy_data_go_settings')
        ),
        confirmText: t('components.logoff.continue_logoff_nickname', { nickname }),
        confirm: resolve,
        cancelText: t('common.cancel'),
        cancel: () => {
          reject(new Error('cancel'));
        },
      });
    });
  }, [editor, openDeleteSpace, openModal, t]);
};

/** 注销警告 */
export const useOpenConfirmDialog = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  return useCallback(
    (text: string) => {
      return new Promise<void>((resolve, reject) => {
        let value = '';
        openModal.warning({
          colorType: 'red',
          maxWidth: 400,
          title: t('components.logoff.permanently_delete_account'),
          className: 'select-none',
          content: (
            <>
              <div className="text-t2 text-grey3">
                {t('components.logoff.permanently_delete_account_warning')}
              </div>
              <div className="py-5 text-t2-bold">
                {t('components.logoff.please_enter_text', { text })}
              </div>
              <Input className="h-10" autoFocus placeholder={text} onChange={(v) => (value = v)} />
            </>
          ),
          confirmText: t('common.continue'),
          confirm: () => {
            if (value.toLocaleLowerCase() === text.toLocaleLowerCase()) {
              resolve();
            } else {
              message.error(t('components.logoff.input_error'));
              throw new Error('confirm');
            }
          },
          cancelText: t('common.cancel'),
          cancel: () => {
            reject(new Error('cancel'));
          },
        });
      });
    },
    [openModal, t]
  );
};

/** 注销须知 */
const useOpenMustKnowDialog = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  return useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      openModal.warning({
        colorType: 'active',
        maxWidth: 400,
        title: __BUILD_IN__ ? (
          t('components.logoff.must_know_title')
        ) : (
          <HelperIconBox
            popup={t('components.logoff.view_logoff_help')}
            link={helperLink.cancelAccount}
          >
            {t('components.logoff.must_know_title')}
          </HelperIconBox>
        ),
        content: (
          <div className="flex flex-col items-start text-left">
            <div>{t('components.logoff.dear_user_regret')}</div>
            <div>{t('components.logoff.before_submit_logoff')}</div>
            <ul
              className="ml-4 mt-5"
              style={{
                listStyle: 'disc',
              }}
            >
              <li>{t('components.logoff.benefits_forfeited')}</li>
              <li>{t('components.logoff.dispute_tracking')}</li>
            </ul>
          </div>
        ),
        confirmText: t('components.logoff.i_acknowledge'),
        confirm: () => {
          resolve();
        },
        cancelText: t('common.cancel'),
        cancel: () => {
          reject(new Error('cancel'));
        },
      });
    });
  }, [openModal, t]);
};

/** 注销服务对话框 */
const useOpenLogoffServiceDialog = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  return useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      openModal.warning({
        maxWidth: 400,
        colorType: 'active',
        title: t('common.cancel_service'),
        content: (
          <div className="text-left">
            <div>{t('components.logoff.ensure_data_handled')}</div>
            <ul
              className="ml-4 mt-5"
              style={{
                listStyle: 'disc',
              }}
            >
              <li>{t('components.logoff.auto_exit_multi_spaces')}</li>
              <li>{t('components.logoff.only_own_space_deleted')}</li>
            </ul>
          </div>
        ),
        confirmText: t('common.continue'),
        confirm: () => {
          resolve();
        },
        cancelText: t('common.cancel'),
        cancel: () => {
          reject(new Error('cancel'));
        },
      });
    });
  }, [openModal, t]);
};

/** 注销服务填理由 */
const useOpenLogoffReasonDialog = () => {
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  return useCallback(() => {
    return new Promise<LogoffReasonInfo>((resolve, reject) => {
      const { modalId } = openModal.modal({
        autoClose: false,
        keyboard: false,
        content: () => (
          <LogoffReason
            onCancel={() => {
              reject(new Error('cancel'));
              closeModal(modalId);
            }}
            onNext={(reasons, otherReason) => {
              const selectReason: string[] = [];
              reasons.forEach((r, index) => {
                if (r) selectReason.push(index.toString());
              });
              resolve({ reasons: selectReason, otherReason });
              closeModal(modalId);
            }}
          />
        ),
      });
    });
  }, [closeModal, openModal]);
};

const useOpenReceiveCaptchaDialog = () => {
  const openModal = useOpenModal();
  const closeModal = useCloseModal();

  return useCallback(
    (reasonInfo: LogoffReasonInfo) => {
      return new Promise<void>((resolve, reject) => {
        const { modalId } = openModal.modal({
          autoClose: false,
          keyboard: false,
          content: () => {
            return (
              <CaptchaPanel
                reasonInfo={reasonInfo}
                account={$currentUserCache.phone ?? $currentUserCache.email}
                onConfirm={() => {
                  closeModal(modalId);
                  resolve();
                }}
                cancel={() => {
                  closeModal(modalId);
                  reject(new Error('cancel'));
                }}
              />
            );
          },
        });
      });
    },
    [closeModal, openModal]
  );
};
